import React from 'react';
import classNames from 'classnames';

/**
 * Simple Tab Item that doesn't require content to be defined
 */
export interface SimpleTabItem {
  id: string;
  label: string;
  icon?: string;
  count?: number;
  disabled?: boolean;
}

export interface SimpleTabsProps {
  tabs: SimpleTabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
  variant?: 'default' | 'contained' | 'pills';
  color?: 'primary' | 'secondary' | 'neutral';
  fullWidth?: boolean;
  orientation?: 'horizontal' | 'vertical';
  size?: 'small' | 'medium' | 'large';
  testId?: string;
}

/**
 * A simpler version of the Tabs component that doesn't manage its own state
 * or require content to be defined in the tab items.
 * 
 * This component is useful for tabs that just change the display of content
 * managed outside the tabs themselves, like in search results or filtering.
 */
const SimpleTabs: React.FC<SimpleTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  className = '',
  variant = 'default',
  color = 'primary',
  fullWidth = false,
  orientation = 'horizontal',
  size = 'medium',
  testId,
}) => {
  const handleTabClick = (tabId: string) => {
    onChange(tabId);
  };

  // Get the color classes based on variant and color
  const getColorClasses = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) {
      return 'text-gray-400 dark:text-gray-400 cursor-not-allowed';
    }

    if (isActive) {
      switch (variant) {
        case 'contained':
          switch (color) {
            case 'primary':
              return 'bg-blue-600 text-white';
            case 'secondary':
              return 'bg-purple-600 text-white';
            case 'neutral':
              return 'bg-gray-700 dark:bg-gray-600 text-white';
            default:
              return 'bg-blue-600 text-white';
          }
        case 'pills':
          switch (color) {
            case 'primary':
              return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100';
            case 'secondary':
              return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100';
            case 'neutral':
              return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
            default:
              return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100';
          }
        default:
          switch (color) {
            case 'primary':
              return 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400';
            case 'secondary':
              return 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400';
            case 'neutral':
              return 'text-gray-800 dark:text-gray-200 border-b-2 border-gray-800 dark:border-gray-200';
            default:
              return 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400';
          }
      }
    } else {
      return 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100';
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'text-xs py-1 px-2';
      case 'large':
        return 'text-lg py-3 px-4';
      default:
        return 'text-sm py-2 px-3';
    }
  };

  return (
    <div
      className={classNames(
        'flex',
        {
          'flex-row': orientation === 'horizontal',
          'flex-col': orientation === 'vertical',
          'border-b border-gray-200 dark:border-gray-700': variant === 'default' && orientation === 'horizontal',
          'border-r border-gray-200 dark:border-gray-700': variant === 'default' && orientation === 'vertical',
          'bg-gray-100 dark:bg-gray-800 p-1 rounded-md': variant === 'contained' || variant === 'pills',
          'justify-between': fullWidth && orientation === 'horizontal',
        },
        className
      )}
      role="tablist"
      data-testid={testId}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`tabpanel-${tab.id}`}
          id={`tab-${tab.id}`}
          disabled={tab.disabled}
          className={classNames(
            'font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
            getSizeClasses(),
            getColorClasses(activeTab === tab.id, !!tab.disabled),
            {
              'flex-grow text-center': fullWidth && orientation === 'horizontal',
              'rounded-md': variant === 'pills',
              'first:rounded-l-md last:rounded-r-md': variant === 'contained' && orientation === 'horizontal',
              'first:rounded-t-md last:rounded-b-md': variant === 'contained' && orientation === 'vertical',
              'mr-4': orientation === 'horizontal' && variant === 'default',
              'mb-4': orientation === 'vertical' && variant === 'default',
              'mr-1': orientation === 'horizontal' && (variant === 'contained' || variant === 'pills'),
              'mb-1': orientation === 'vertical' && (variant === 'contained' || variant === 'pills'),
            }
          )}
          onClick={() => !tab.disabled && handleTabClick(tab.id)}
        >
          {tab.icon && (
            <span className="mr-2">
              {/* Icon implementation */}
              {tab.icon}
            </span>
          )}
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default SimpleTabs;